import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Calendar, X, Check, Loader2, Edit3, Repeat, Circle, Bell, RotateCcw
} from 'lucide-react';
import { API, getAuth, PRIORITY_CONFIG, CATEGORY_CONFIG, RECURRENCE_OPTIONS, REMINDER_OPTIONS } from './taskConfigs';

// Helper: compute remind_at from due_date + due_time - minutes offset
const computeRemindAt = (dueDate, dueTime, minutesBefore) => {
  if (!dueDate) return null;
  const dt = new Date(`${dueDate}T${dueTime || '09:00'}`);
  dt.setMinutes(dt.getMinutes() - minutesBefore);
  return dt.toISOString();
};

// ─── Create/Edit Task Modal (extracted to prevent re-mount on parent re-render) ───
export const TaskModal = ({ editingTask, setEditingTask, setShowCreateModal, templates, users, user, refreshAll }) => {
  const existingReminder = editingTask?.reminders?.[0]?.type || 'none';
  const existingCustomDt = editingTask?.reminders?.[0]?.type === 'custom' ? editingTask.reminders[0].remind_at?.slice(0, 16) : '';
  const [form, setForm] = useState(editingTask || { title: '', description: '', category: 'custom', priority: 'medium', assigned_to: user?.id, due_date: '', due_time: '', checklist: [], recurrence: 'none' });
  const [saving, setSaving] = useState(false);
  const [newCheckItem, setNewCheckItem] = useState('');
  const [showTplPicker, setShowTplPicker] = useState(!editingTask?.id);
  const [reminderType, setReminderType] = useState(existingReminder);
  const [customReminderDt, setCustomReminderDt] = useState(existingCustomDt);

  const applyTemplate = (tpl) => {
    setForm(f => ({
      ...f,
      title: tpl.name,
      description: tpl.description || '',
      category: tpl.category || 'custom',
      priority: tpl.priority || 'medium',
      checklist: (tpl.checklist || []).map(c => ({ text: c.text, done: false })),
    }));
    setShowTplPicker(false);
  };

  const handleSubmit = async () => {
    if (!form.title?.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_to: form.assigned_to?.id || form.assigned_to };
      // Build reminders array
      const reminders = [];
      if (reminderType !== 'none') {
        const opt = REMINDER_OPTIONS.find(o => o.value === reminderType);
        if (reminderType === 'custom' && customReminderDt) {
          reminders.push({ type: 'custom', label: 'Custom', remind_at: new Date(customReminderDt).toISOString() });
        } else if (opt?.minutes && form.due_date) {
          const remindAt = computeRemindAt(form.due_date, form.due_time, opt.minutes);
          if (remindAt) reminders.push({ type: reminderType, label: opt.label, remind_at: remindAt });
        }
      }
      payload.reminders = reminders;
      if (editingTask?.id) {
        await axios.put(`${API}/api/tasks/${editingTask.id}`, payload, getAuth());
        toast.success('Task updated');
      } else {
        await axios.post(`${API}/api/tasks`, payload, getAuth());
        toast.success('Task created');
      }
      setShowCreateModal(false); setEditingTask(null); refreshAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => { setShowCreateModal(false); setEditingTask(null); }}>
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b text-white rounded-t-2xl" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }}>
          <h2 className="font-semibold">{editingTask?.id ? 'Edit Task' : 'New Task'}</h2>
          <button onClick={() => { setShowCreateModal(false); setEditingTask(null); }}><X className="w-5 h-5" /></button>
        </div>

        {/* Template Picker (for new tasks only) */}
        {!editingTask?.id && showTplPicker && templates.length > 0 && (
          <div className="px-5 pt-4 pb-2 border-b bg-gray-50">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs font-medium text-gray-500">Start from template:</span>
              <button onClick={() => setShowTplPicker(false)} className="text-[10px] text-amber-700 hover:underline">Skip</button>
            </div>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {templates.slice(0, 6).map(tpl => (
                <button key={tpl.id} onClick={() => applyTemplate(tpl)} className="shrink-0 px-3 py-2 bg-white border rounded-xl text-xs hover:border-amber-300 hover:bg-amber-50 transition-all text-left min-w-[140px]" data-testid={`tpl-pick-${tpl.id}`}>
                  <div className="font-medium text-gray-700 truncate">{tpl.name}</div>
                  <div className="text-[10px] text-gray-400 mt-0.5">{CATEGORY_CONFIG[tpl.category]?.icon} {tpl.checklist?.length || 0} items</div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="p-5 space-y-3">
          <input value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Task title..." className="w-full px-3 py-2.5 border rounded-xl text-sm font-medium focus:ring-2 focus:ring-amber-200" autoFocus data-testid="task-title-input" />
          <textarea value={form.description || ''} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description (optional)" rows={2} className="w-full px-3 py-2 border rounded-xl text-sm resize-none" />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Category</label>
              <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="w-full px-3 py-2 border rounded-xl text-sm" data-testid="task-category-select">
                {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Priority</label>
              <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="w-full px-3 py-2 border rounded-xl text-sm" data-testid="task-priority-select">
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Due Date</label>
              <input type="date" value={form.due_date || ''} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} className="w-full px-3 py-2 border rounded-xl text-sm" data-testid="task-due-date" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Due Time</label>
              <input type="time" value={form.due_time || ''} onChange={e => setForm(f => ({ ...f, due_time: e.target.value }))} className="w-full px-3 py-2 border rounded-xl text-sm" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 block">Assign To</label>
              <select value={typeof form.assigned_to === 'string' ? form.assigned_to : form.assigned_to?.id || ''} onChange={e => setForm(f => ({ ...f, assigned_to: e.target.value }))} className="w-full px-3 py-2 border rounded-xl text-sm" data-testid="task-assign-select">
                {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.role_name || u.role || 'User'})</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Repeat className="w-3 h-3" /> Recurrence</label>
              <select value={form.recurrence || 'none'} onChange={e => setForm(f => ({ ...f, recurrence: e.target.value }))} className="w-full px-3 py-2 border rounded-xl text-sm" data-testid="task-recurrence-select">
                {RECURRENCE_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
          </div>
          {/* Reminder - Outlook style */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block flex items-center gap-1"><Bell className="w-3 h-3" /> Reminder</label>
            <select value={reminderType} onChange={e => setReminderType(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm" data-testid="task-reminder-select">
              {REMINDER_OPTIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
            {reminderType === 'custom' && (
              <input type="datetime-local" value={customReminderDt} onChange={e => setCustomReminderDt(e.target.value)} className="w-full px-3 py-2 border rounded-xl text-sm mt-2" data-testid="task-reminder-custom-dt" />
            )}
          </div>
          {/* Checklist */}
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Checklist</label>
            {(form.checklist || []).map((item, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <input type="checkbox" checked={item.done} onChange={() => { const cl = [...form.checklist]; cl[i] = { ...cl[i], done: !cl[i].done }; setForm(f => ({ ...f, checklist: cl })); }} className="rounded" />
                <span className={`text-sm flex-1 ${item.done ? 'line-through text-gray-400' : ''}`}>{item.text}</span>
                <button onClick={() => setForm(f => ({ ...f, checklist: f.checklist.filter((_, j) => j !== i) }))} className="text-red-400 hover:text-red-600"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <div className="flex gap-2 mt-2">
              <input value={newCheckItem} onChange={e => setNewCheckItem(e.target.value)} placeholder="Add checklist item..." className="flex-1 px-3 py-2 border rounded-xl text-xs focus:ring-2 focus:ring-amber-200" onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); if (newCheckItem.trim()) { setForm(f => ({ ...f, checklist: [...(f.checklist || []), { text: newCheckItem.trim(), done: false }] })); setNewCheckItem(''); } } }} data-testid="checklist-input" />
              <button type="button" onClick={(e) => { e.preventDefault(); e.stopPropagation(); if (newCheckItem.trim()) { setForm(f => ({ ...f, checklist: [...(f.checklist || []), { text: newCheckItem.trim(), done: false }] })); setNewCheckItem(''); } }} className="px-4 py-2 text-white rounded-xl text-xs font-medium min-w-[48px] active:scale-95 shadow-[0_4px_10px_rgba(217,119,6,0.30)]" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }} data-testid="checklist-add-btn">
                <Plus className="w-4 h-4 inline" /> Add
              </button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={() => { setShowCreateModal(false); setEditingTask(null); }} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
          <button onClick={handleSubmit} disabled={saving} className="px-5 py-2 text-white text-sm rounded-xl shadow-[0_4px_12px_rgba(217,119,6,0.30)] hover:shadow-[0_8px_20px_rgba(217,119,6,0.40)] transition-all duration-180 ease-out hover:scale-[1.02] active:scale-95 disabled:opacity-50 disabled:scale-100 flex items-center gap-2" style={{ background: 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)' }} data-testid="task-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} {editingTask?.id ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Complete Modal (with Reschedule + Re-open) ───
export const CompleteModal = ({ showCompleteModal, setShowCompleteModal, setEditingTask, setShowCreateModal, refreshAll }) => {
  const isAlreadyDone = ['completed', 'partial', 'not_completed'].includes(showCompleteModal?.status);
  const [status, setStatus] = useState(showCompleteModal?.completion_status || 'completed');
  const [remarks, setRemarks] = useState(showCompleteModal?.completion_remarks || '');
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleTime, setRescheduleTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [reopening, setReopening] = useState(false);

  const showReschedule = status === 'not_completed' || status === 'partial';

  // Set default reschedule to next day
  useEffect(() => {
    if (showReschedule && !rescheduleDate) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      setRescheduleDate(tomorrow.toISOString().split('T')[0]);
      setRescheduleTime(showCompleteModal?.due_time || '10:00');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showReschedule]);

  const handleComplete = async () => {
    setSaving(true);
    try {
      const payload = { completion_status: status, remarks };
      if (showReschedule && rescheduleDate) {
        payload.reschedule_date = rescheduleDate;
        payload.reschedule_time = rescheduleTime;
      }
      await axios.post(`${API}/api/tasks/${showCompleteModal.id}/complete`, payload, getAuth());
      const label = showReschedule && rescheduleDate ? `Rescheduled to ${rescheduleDate}` : `Marked as ${status}`;
      toast.success(label);
      setShowCompleteModal(null); refreshAll();
    } catch { toast.error('Failed'); }
    setSaving(false);
  };

  const handleReopen = async () => {
    setReopening(true);
    try {
      await axios.post(`${API}/api/tasks/${showCompleteModal.id}/reopen`, {}, getAuth());
      toast.success('Task re-opened');
      setShowCompleteModal(null); refreshAll();
    } catch { toast.error('Failed to re-open'); }
    setReopening(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={() => setShowCompleteModal(null)}>
      <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b bg-green-700 text-white rounded-t-2xl flex items-center justify-between sticky top-0 z-10">
          <span className="font-semibold">{isAlreadyDone ? 'Task Details' : 'Complete Task'}</span>
          <div className="flex items-center gap-3">
            {showCompleteModal?.assigned_to?.name && (
              <span className="text-[11px] text-green-200">Assigned: {showCompleteModal.assigned_to.name}</span>
            )}
            <button onClick={() => setShowCompleteModal(null)} className="text-white/70 hover:text-white text-xl leading-none">&times;</button>
          </div>
        </div>
        <div className="p-5 space-y-3">
          <p className="text-sm font-semibold text-gray-800">{showCompleteModal?.title}</p>
          {showCompleteModal?.description && (
            <p className="text-xs text-gray-500 leading-relaxed">{showCompleteModal.description}</p>
          )}
          {/* Task info pills */}
          <div className="flex flex-wrap gap-2 text-[10px]">
            {showCompleteModal?.due_date && (
              <span className="px-2 py-0.5 bg-blue-50 border border-blue-200 rounded-full text-blue-600">Due: {showCompleteModal.due_date} {showCompleteModal.due_time || ''}</span>
            )}
            {showCompleteModal?.priority && (
              <span className={`px-2 py-0.5 rounded-full capitalize border ${showCompleteModal.priority === 'high' ? 'bg-red-50 border-red-200 text-red-600' : showCompleteModal.priority === 'medium' ? 'bg-amber-50 border-amber-200 text-amber-600' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>{showCompleteModal.priority}</span>
            )}
            {showCompleteModal?.category && (
              <span className="px-2 py-0.5 bg-purple-50 border border-purple-200 rounded-full text-purple-600">{showCompleteModal.category}</span>
            )}
          </div>
          {/* Previous completion remarks if any */}
          {showCompleteModal?.completion_remarks && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <p className="text-[10px] text-gray-400 mb-1">Previous Remarks:</p>
              <p className="text-xs text-gray-700 leading-relaxed">{showCompleteModal.completion_remarks}</p>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Status</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { v: 'completed', l: 'Completed', c: 'bg-green-100 text-green-700 border-green-300' },
                { v: 'partial', l: 'Partial Pending', c: 'bg-amber-100 text-amber-700 border-amber-300' },
                { v: 'not_completed', l: 'Not Completed', c: 'bg-red-100 text-red-700 border-red-300' },
              ].map(s => (
                <button key={s.v} onClick={() => setStatus(s.v)} className={`px-3 py-1.5 rounded-xl text-xs font-medium border ${status === s.v ? s.c + ' ring-2 ring-offset-1' : 'bg-gray-50 text-gray-500 border-gray-200'}`} data-testid={`complete-status-${s.v}`}>
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {/* Reschedule Section */}
          {showReschedule && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-xs font-medium text-amber-700">
                <Calendar className="w-3.5 h-3.5" /> Reschedule to complete later
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-[10px] text-amber-600 mb-0.5 block">New Date</label>
                  <input type="date" value={rescheduleDate} onChange={e => setRescheduleDate(e.target.value)} className="w-full px-2 py-1.5 border border-amber-200 rounded-lg text-xs bg-white" data-testid="reschedule-date" />
                </div>
                <div>
                  <label className="text-[10px] text-amber-600 mb-0.5 block">New Time</label>
                  <input type="time" value={rescheduleTime} onChange={e => setRescheduleTime(e.target.value)} className="w-full px-2 py-1.5 border border-amber-200 rounded-lg text-xs bg-white" data-testid="reschedule-time" />
                </div>
              </div>
            </div>
          )}

          <div>
            <label className="text-xs text-gray-500 mb-1 block">Remarks</label>
            <textarea value={remarks} onChange={e => setRemarks(e.target.value)} rows={3} placeholder="Add remarks about completion..." className="w-full px-3 py-2 border rounded-xl text-sm resize-none" data-testid="complete-remarks" />
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-between">
          <div className="flex gap-2">
            <button type="button" onClick={() => { setEditingTask(showCompleteModal); setShowCreateModal(true); setShowCompleteModal(null); }} className="px-3 py-2 text-sm text-amber-700 hover:bg-amber-50 rounded-xl flex items-center gap-1.5 font-medium" data-testid="complete-edit-btn">
              <Edit3 className="w-3.5 h-3.5" /> Edit
            </button>
            {isAlreadyDone && (
              <button type="button" onClick={handleReopen} disabled={reopening} className="px-3 py-2 text-sm text-orange-600 hover:bg-orange-50 rounded-xl flex items-center gap-1.5 font-medium" data-testid="complete-reopen-btn">
                <RotateCcw className="w-3.5 h-3.5" /> {reopening ? 'Re-opening...' : 'Re-open'}
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setShowCompleteModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
            <button onClick={handleComplete} disabled={saving} className="px-5 py-2 bg-green-700 text-white text-sm rounded-xl hover:bg-green-600 disabled:opacity-50" data-testid="complete-submit-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> : null} {showReschedule && rescheduleDate ? 'Reschedule' : 'Submit'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─── Create Template Modal ───
export const CreateTemplateModal = ({ setShowTemplateModal, fetchTemplates }) => {
  const [form, setForm] = useState({ name: '', description: '', category: 'custom', priority: 'medium', due_offset_hours: 24, checklist: [] });
  const [saving, setSaving] = useState(false);
  const [newItem, setNewItem] = useState('');

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Template name required'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/tasks/templates`, form, getAuth());
      toast.success('Template created');
      setShowTemplateModal(false);
      fetchTemplates();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowTemplateModal(false)}>
      <div className="bg-white rounded-2xl w-full max-w-md max-h-[85vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b text-white rounded-t-2xl font-semibold" style={{ background: 'linear-gradient(135deg, #D97706 0%, #EA580C 100%)' }}>New Template</div>
        <div className="p-5 space-y-3">
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Template name..." className="w-full px-3 py-2.5 border rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-200" autoFocus data-testid="template-name-input" />
          <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" rows={2} className="w-full px-3 py-2 border rounded-xl text-sm resize-none" />
          <div className="grid grid-cols-3 gap-2">
            <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className="px-2 py-2 border rounded-xl text-xs">
              {Object.entries(CATEGORY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
            </select>
            <select value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))} className="px-2 py-2 border rounded-xl text-xs">
              {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <input type="number" value={form.due_offset_hours} onChange={e => setForm(f => ({ ...f, due_offset_hours: parseInt(e.target.value) || 24 }))} className="px-2 py-2 border rounded-xl text-xs" placeholder="Hours" />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block">Checklist Items</label>
            {form.checklist.map((item, i) => (
              <div key={i} className="flex items-center gap-2 mb-1">
                <Circle className="w-3 h-3 text-gray-300" />
                <span className="text-xs flex-1">{item.text}</span>
                <button onClick={() => setForm(f => ({ ...f, checklist: f.checklist.filter((_, j) => j !== i) }))} className="text-red-400"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <div className="flex gap-2 mt-1">
              <input value={newItem} onChange={e => setNewItem(e.target.value)} placeholder="Add item..." className="flex-1 px-2 py-1 border rounded-lg text-xs" onKeyDown={e => { if (e.key === 'Enter' && newItem.trim()) { setForm(f => ({ ...f, checklist: [...f.checklist, { text: newItem.trim(), done: false }] })); setNewItem(''); } }} />
              <button onClick={() => { if (newItem.trim()) { setForm(f => ({ ...f, checklist: [...f.checklist, { text: newItem.trim(), done: false }] })); setNewItem(''); } }} className="px-2 py-1 bg-blue-100 text-blue-700 rounded-lg text-xs">Add</button>
            </div>
          </div>
        </div>
        <div className="px-5 py-3 border-t flex justify-end gap-2">
          <button onClick={() => setShowTemplateModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="px-5 py-2 bg-slate-800 text-white text-sm rounded-xl hover:bg-slate-700 disabled:opacity-50" data-testid="template-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save Template
          </button>
        </div>
      </div>
    </div>
  );
};
